import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import LoanerCreateLoan from './LoanerCreateLoan';
import PaymentRecorder from './PaymentRecorder';
import '../styles/Dashboard.css';

function LoanerDashboard() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [loanRequests, setLoanRequests] = useState([]);
  const [loans, setLoans] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPaymentRecorder, setShowPaymentRecorder] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  async function fetchData() {
    try {
      await Promise.all([fetchLoanRequests(), fetchLoans()]);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchLoanRequests() {
    // Fetch both pending loan requests and rejected loans
    const requestsQuery = query(
      collection(db, 'loanRequests'),
      where('status', '==', 'pending'),
      where('assignedLoanerId', '==', currentUser.uid)
    );
    const requestsSnapshot = await getDocs(requestsQuery);
    const requestsList = requestsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      type: 'request'
    }));

    // Fetch rejected loans
    const rejectedLoansQuery = query(
      collection(db, 'loans'),
      where('status', '==', 'rejected'),
      where('loanerId', '==', currentUser.uid)
    );
    const rejectedSnapshot = await getDocs(rejectedLoansQuery);
    const rejectedList = rejectedSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      type: 'rejected'
    }));

    // Combine and sort by date
    const combined = [...requestsList, ...rejectedList].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.rejectedAt);
      const dateB = new Date(b.createdAt || b.rejectedAt);
      return dateB - dateA; // Most recent first
    });

    setLoanRequests(combined);
  }

  async function fetchLoans() {
    const loansQuery = query(
      collection(db, 'loans'),
      where('loanerId', '==', currentUser.uid)
    );
    const snapshot = await getDocs(loansQuery);
    const loansList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setLoans(loansList);
  }

  async function handleConvertRequest(request) {
    setSelectedRequest(request);
    setShowCreateForm(true);
  }

  async function handleDeleteRequest(requestId) {
    if (window.confirm('Are you sure you want to delete this request?')) {
      try {
        await deleteDoc(doc(db, 'loanRequests', requestId));
        fetchLoanRequests();
      } catch (err) {
        console.error('Error deleting request:', err);
      }
    }
  }

  function handleCreateSuccess() {
    setShowCreateForm(false);
    setSelectedRequest(null);
    fetchData();
  }

  function handlePaymentSuccess() {
    setShowPaymentRecorder(false);
    setSelectedLoan(null);
    fetchLoans();
  }

  function getAdjustedAmortizationSchedule(loan) {
    if (!loan.payments || loan.payments.length === 0) {
      return loan.amortizationSchedule || [];
    }

    // Sort payments by date
    const sortedPayments = [...loan.payments].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    let remainingBalance = loan.amount;
    const monthlyRate = loan.interestRate / 100 / 12;
    const adjustedSchedule = [];
    let paymentIndex = 0;

    for (let period = 1; period <= loan.termLength; period++) {
      // Stop if balance is already zero
      if (remainingBalance <= 0.01) {
        break;
      }

      // Check if there's an actual payment for this period
      const actualPayment = sortedPayments[paymentIndex];
      
      if (actualPayment && paymentIndex < sortedPayments.length) {
        // Use actual payment data
        const interestPortion = remainingBalance * monthlyRate;
        const principalPortion = actualPayment.amount - interestPortion;
        remainingBalance = Math.max(0, remainingBalance - principalPortion);

        adjustedSchedule.push({
          period,
          paymentDate: actualPayment.date,
          payment: actualPayment.amount,
          interest: interestPortion,
          principal: principalPortion,
          balance: remainingBalance,
          isPaid: true
        });

        paymentIndex++;
      } else {
        // Use projected payment data
        const originalRow = loan.amortizationSchedule?.[period - 1];
        if (originalRow) {
          // Recalculate based on current remaining balance
          const interestPortion = remainingBalance * monthlyRate;
          
          // Check if this is the final payment
          const totalOwed = remainingBalance + interestPortion;
          let paymentAmount;
          let principalPortion;
          let newBalance;
          
          if (totalOwed < loan.monthlyPayment) {
            // Final payment - only pay what's owed
            paymentAmount = totalOwed;
            principalPortion = remainingBalance;
            newBalance = 0;
          } else {
            // Regular payment
            paymentAmount = loan.monthlyPayment;
            principalPortion = paymentAmount - interestPortion;
            newBalance = Math.max(0, remainingBalance - principalPortion);
          }

          adjustedSchedule.push({
            period,
            paymentDate: originalRow.paymentDate,
            payment: paymentAmount,
            interest: interestPortion,
            principal: principalPortion,
            balance: newBalance,
            isPaid: false
          });

          remainingBalance = newBalance;
        }
      }
    }

    return adjustedSchedule;
  }

  function calculateTotalLoaned() {
    return loans.filter(l => l.status !== 'rejected').reduce((sum, loan) => sum + loan.amount, 0);
  }

  function calculateTotalOutstanding() {
    return loans.filter(l => l.status === 'active' || l.status === 'pending_acceptance').reduce((sum, loan) => sum + loan.remainingBalance, 0);
  }

  function calculateTotalMonthlyPayments() {
    return loans.filter(l => l.status === 'active').reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Loaner Dashboard</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedRequest(null);
            setShowCreateForm(true);
          }}
        >
          Create New Loan
        </button>
      </div>

      <div className="stats-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem'}}>
        <div className="stat-card">
          <h3>Total Loaned</h3>
          <p className="stat-value">${calculateTotalLoaned().toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Total Outstanding</h3>
          <p className="stat-value">${calculateTotalOutstanding().toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Total Monthly Payments</h3>
          <p className="stat-value">${calculateTotalMonthlyPayments().toFixed(2)}</p>
        </div>
      </div>

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCreateForm(false)}>×</button>
            <LoanerCreateLoan
              requestToConvert={selectedRequest}
              onSuccess={handleCreateSuccess}
            />
          </div>
        </div>
      )}

      {showPaymentRecorder && selectedLoan && (
        <div className="modal-overlay" onClick={() => setShowPaymentRecorder(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPaymentRecorder(false)}>×</button>
            <PaymentRecorder
              loan={selectedLoan}
              onSuccess={handlePaymentSuccess}
            />
          </div>
        </div>
      )}

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Loan Requests ({loanRequests.length})
        </button>
        <button
          className={`tab ${activeTab === 'loans' ? 'active' : ''}`}
          onClick={() => setActiveTab('loans')}
        >
          My Loans ({loans.length})
        </button>
      </div>

      {activeTab === 'requests' && (
        <div className="tab-content">
          <h2>Pending Loan Requests</h2>
          {loanRequests.length === 0 ? (
            <div className="empty-state">
              <p>No pending loan requests.</p>
            </div>
          ) : (
            <div className="loan-cards">
              {loanRequests.map(request => (
                <div key={request.id} className="loan-card">
                  <div className="loan-card-header">
                    <h3>${request.amount.toFixed(2)}</h3>
                    <span className={`status-badge ${request.type === 'rejected' ? 'status-rejected' : 'status-pending'}`}>
                      {request.type === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </div>
                  <div className="loan-card-body">
                    <div className="loan-detail">
                      <span className="label">{request.type === 'rejected' ? 'Borrower:' : 'Requested by:'}</span>
                      <span className="value">{request.loaneeName}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Email:</span>
                      <span className="value">{request.loaneeEmail}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Purpose:</span>
                      <span className="value">{request.purpose}</span>
                    </div>
                    {request.type === 'rejected' && request.rejectionReason && (
                      <div className="loan-detail">
                        <span className="label">Rejection Reason:</span>
                        <span className="value" style={{color: 'var(--error-color)', fontWeight: '500'}}>{request.rejectionReason}</span>
                      </div>
                    )}
                    {request.type === 'rejected' && request.rejectedBy && (
                      <div className="loan-detail">
                        <span className="label">Rejected by:</span>
                        <span className="value">{request.rejectedBy}</span>
                      </div>
                    )}
                    {request.notes && request.type !== 'rejected' && (
                      <div className="loan-detail">
                        <span className="label">Notes:</span>
                        <span className="value">{request.notes}</span>
                      </div>
                    )}
                    <div className="loan-detail">
                      <span className="label">{request.type === 'rejected' ? 'Rejected on:' : 'Requested on:'}</span>
                      <span className="value">{new Date(request.type === 'rejected' ? request.rejectedAt : request.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="loan-card-footer">
                    {request.type === 'rejected' ? (
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleConvertRequest(request)}
                        >
                          Create Revised Loan Offer
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleConvertRequest(request)}
                        >
                          Convert to Loan
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteRequest(request.id)}
                        >
                          Delete Request
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'loans' && (
        <div className="tab-content">
          <h2>My Loans</h2>
          {loans.length === 0 ? (
            <div className="empty-state">
              <p>You haven't created any loans yet.</p>
            </div>
          ) : (
            <div className="loan-cards">
              {loans.map(loan => (
                <div key={loan.id} className="loan-card">
                  <div className="loan-card-header">
                    <h3>${loan.amount.toFixed(2)}</h3>
                    <span className={`status-badge status-${loan.status === 'active' ? 'active' : loan.status === 'paid_off' ? 'paid' : 'pending'}`}>
                      {loan.status === 'pending_acceptance' ? 'Pending Acceptance' :
                       loan.status === 'active' ? 'Active' :
                       loan.status === 'paid_off' ? 'Paid Off' : loan.status}
                    </span>
                  </div>
                  <div className="loan-card-body">
                    <div className="loan-detail">
                      <span className="label">Loanee:</span>
                      <span className="value">{loan.loaneeName}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Purpose:</span>
                      <span className="value">{loan.purpose}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Interest Rate:</span>
                      <span className="value">{loan.interestRate}%</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Monthly Payment:</span>
                      <span className="value">${loan.monthlyPayment.toFixed(2)}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Total Cost of Loan:</span>
                      <span className="value">${(loan.amount + loan.totalInterest).toFixed(2)}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Total Interest:</span>
                      <span className="value">${loan.totalInterest.toFixed(2)}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Remaining Balance:</span>
                      <span className="value highlight">${loan.remainingBalance.toFixed(2)}</span>
                    </div>
                    <div className="loan-detail">
                      <span className="label">Payments Made:</span>
                      <span className="value">{loan.payments?.length || 0}</span>
                    </div>
                  </div>
                  <div className="loan-card-footer">
                    {loan.status === 'active' && (
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setSelectedLoan(loan);
                          setShowPaymentRecorder(true);
                        }}
                      >
                        Record Payment
                      </button>
                    )}
                    <button
                      className="btn btn-link"
                      onClick={() => setExpandedLoan(expandedLoan?.id === loan.id ? null : loan)}
                    >
                      {expandedLoan?.id === loan.id ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>

                  {expandedLoan?.id === loan.id && (
                    <div className="loan-details-expanded">
                      <h4>Amortization Schedule {loan.payments && loan.payments.length > 0 && '(Adjusted for Payments)'}</h4>
                      <div className="table-wrapper">
                        <table className="amortization-table">
                          <thead>
                            <tr>
                              <th>Status</th>
                              <th>Date</th>
                              <th>Payment</th>
                              <th>Principal</th>
                              <th>Interest</th>
                              <th>Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getAdjustedAmortizationSchedule(loan).map((row) => (
                              <tr key={row.period} style={{backgroundColor: row.isPaid ? '#e8f5e0' : 'transparent'}}>
                                <td>{row.isPaid ? '✓ Paid' : 'Pending'}</td>
                                <td>{row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : `Period ${row.period}`}</td>
                                <td>${row.payment.toFixed(2)}</td>
                                <td>${row.principal.toFixed(2)}</td>
                                <td>${row.interest.toFixed(2)}</td>
                                <td>${row.balance.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {loan.payments && loan.payments.length > 0 && (
                        <div className="payment-history-section">
                          <h4>Payment History</h4>
                          <div className="payment-list">
                            {loan.payments.map((payment, index) => (
                              <div key={index} className="payment-item">
                                <div className="payment-header">
                                  <span className="payment-amount">${payment.amount.toFixed(2)}</span>
                                  <span className="payment-date">{new Date(payment.date).toLocaleDateString()}</span>
                                </div>
                                {payment.notes && (
                                  <div className="payment-notes">{payment.notes}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LoanerDashboard;
